function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function roundToHundredth(value) {
  return Math.round(value * 100) / 100;
}

const MARKER_SCALE_RATE = 1;
const MIN_MARKER_SCALE = 0.55;
const MAX_MARKER_SCALE = 1.35;

const DEFAULT_MARKER_SIZE = 22;
const BULK_SELECTED_MARKER_SIZE = 27;
const SELECTED_MARKER_SIZE = 32;

const MIN_DEFAULT_MARKER_SIZE = 13;
const MIN_BULK_SELECTED_MARKER_SIZE = 16;
const MIN_SELECTED_MARKER_SIZE = 20;

const MAX_DEFAULT_MARKER_SIZE = 30;
const MAX_BULK_SELECTED_MARKER_SIZE = 36.5;
const MAX_SELECTED_MARKER_SIZE = 43;

const MIN_MARKER_BORDER_WIDTH = 1.75;
const MAX_MARKER_BORDER_WIDTH = 4.5;
const ROOM_LABEL_BASE_GAP = 8;
const ROOM_LABEL_MIN_GAP = 4;
const ROOM_LABEL_MAX_GAP = 12;

export function getMarkerScaleFactor(currentZoom, baseZoom) {
  if (!Number.isFinite(currentZoom) || !Number.isFinite(baseZoom)) {
    return 1;
  }

  const zoomDiff = currentZoom - baseZoom;
  const scale = Math.pow(2, zoomDiff * MARKER_SCALE_RATE);

  return clamp(scale, MIN_MARKER_SCALE, MAX_MARKER_SCALE);
}

export function getLiveMapMarkerSize({ currentZoom, baseZoom, isSelected = false, isBulkSelected = false } = {}) {
  const scaleFactor = getMarkerScaleFactor(currentZoom, baseZoom);

  if (isSelected) {
    return roundToHundredth(
      clamp(SELECTED_MARKER_SIZE * scaleFactor, MIN_SELECTED_MARKER_SIZE, MAX_SELECTED_MARKER_SIZE),
    );
  }

  if (isBulkSelected) {
    return roundToHundredth(
      clamp(BULK_SELECTED_MARKER_SIZE * scaleFactor, MIN_BULK_SELECTED_MARKER_SIZE, MAX_BULK_SELECTED_MARKER_SIZE),
    );
  }

  return roundToHundredth(clamp(DEFAULT_MARKER_SIZE * scaleFactor, MIN_DEFAULT_MARKER_SIZE, MAX_DEFAULT_MARKER_SIZE));
}

export function getMarkerBorderWidth(markerSize) {
  return roundToHundredth(clamp(markerSize * 0.11, MIN_MARKER_BORDER_WIDTH, MAX_MARKER_BORDER_WIDTH));
}

export function getMarkerInnerSize(markerSize) {
  return roundToHundredth(Math.max(markerSize - getMarkerBorderWidth(markerSize) * 2, 10));
}

export function getAvatarBorderWidth(markerSize) {
  return getMarkerBorderWidth(markerSize);
}

export function getAvatarInnerSize(markerSize) {
  return getMarkerInnerSize(markerSize);
}

export function getAvatarPhotoSize(markerSize) {
  return roundToHundredth(Math.max(markerSize - getMarkerBorderWidth(markerSize), 10));
}

export function getMarkerGlyphSize(markerSize, variant = 'feature') {
  const scale =
    {
      // Parking and POI glyphs were authored against the full circular marker canvas.
      desk: 1,
      parking: 1,
      feature: 1,
    }[variant] || 1;

  return roundToHundredth(Math.max(markerSize * scale, 8));
}

export function getAvatarMonogramFontSize(markerSize, initialsLength = 1) {
  const innerSize = getMarkerInnerSize(markerSize);
  const scale = initialsLength >= 4 ? 0.38 : initialsLength === 3 ? 0.46 : 0.54;

  return roundToHundredth(Math.max(innerSize * scale, 6));
}

export function getAvatarMonogramVerticalOffset(markerSize) {
  const fontSize = getAvatarMonogramFontSize(markerSize, 1);
  return roundToHundredth(fontSize * 0.16);
}

export function getRoomLabelGap(currentZoom, baseZoom) {
  return roundToHundredth(
    clamp(ROOM_LABEL_BASE_GAP * getMarkerScaleFactor(currentZoom, baseZoom), ROOM_LABEL_MIN_GAP, ROOM_LABEL_MAX_GAP),
  );
}
